"use client";

import { useEffect, useRef, useState } from "react";
import { writeConsent } from "@/lib/consent/storage";
import { trackConsentEvent } from "@/lib/consent/track";
import { type ConsentChoices, ONLY_NECESSARY } from "@/lib/consent/types";

type Strings = {
	title: string;
	description: string;
	necessaryTitle: string;
	necessaryDesc: string;
	analyticsTitle: string;
	analyticsDesc: string;
	marketingTitle: string;
	marketingDesc: string;
	preferencesTitle: string;
	preferencesDesc: string;
	alwaysOn: string;
	save: string;
	cancel: string;
	close: string;
};

const EN: Strings = {
	title: "Cookie preferences",
	description:
		"Choose which cookies we may use. Necessary cookies keep you signed in and the site working. You can change these any time from the footer.",
	necessaryTitle: "Necessary",
	necessaryDesc:
		"Sign-in session, security, core site features, and first-party feed personalization.",
	analyticsTitle: "Analytics",
	analyticsDesc:
		"Controls linking your activity to your email or name. Basic product measurement and session replay, with no name or email (which jobs and pages people use; all form input masked), run as a strictly-functional feature on a legitimate-interest basis; in regions that require consent (EU, UK, Switzerland) turning this off disables them too. (PostHog)",
	marketingTitle: "Marketing & attribution",
	marketingDesc:
		"Remembers which campaign or referrer brought you here so we know which channels work.",
	preferencesTitle: "Preferences",
	preferencesDesc:
		"Remembers things like recently viewed jobs and dismissed banners between visits.",
	alwaysOn: "Always on",
	save: "Save preferences",
	cancel: "Cancel",
	close: "Close",
};

const ID: Strings = {
	title: "Preferensi cookie",
	description:
		"Pilih cookie yang boleh kami gunakan. Cookie yang diperlukan menjaga Anda tetap masuk dan situs berjalan normal. Anda dapat mengubah pilihan ini kapan saja dari footer.",
	necessaryTitle: "Diperlukan",
	necessaryDesc:
		"Sesi login, keamanan, fitur inti situs, dan personalisasi feed pihak pertama.",
	analyticsTitle: "Analitik",
	analyticsDesc:
		"Mengontrol pengaitan aktivitas Anda dengan email atau nama. Pengukuran produk dasar dan rekaman sesi, tanpa nama atau email (lowongan dan halaman yang digunakan; seluruh input formulir disamarkan), berjalan sebagai fitur fungsional atas dasar kepentingan sah; di wilayah yang mewajibkan persetujuan (UE, Inggris, Swiss) menonaktifkan ini juga menonaktifkan keduanya. (PostHog)",
	marketingTitle: "Marketing & atribusi",
	marketingDesc:
		"Mengingat kampanye atau referrer yang membawa Anda ke sini agar kami tahu kanal mana yang efektif.",
	preferencesTitle: "Preferensi",
	preferencesDesc:
		"Mengingat hal seperti lowongan yang baru dilihat dan banner yang Anda tutup antar kunjungan.",
	alwaysOn: "Selalu aktif",
	save: "Simpan preferensi",
	cancel: "Batal",
	close: "Tutup",
};

export type CookiePreferencesModalProps = {
	isOpen: boolean;
	onClose: () => void;
	locale?: string;
	initial?: ConsentChoices;
};

export function CookiePreferencesModal({
	isOpen,
	onClose,
	locale,
	initial,
}: CookiePreferencesModalProps) {
	const t = locale === "id" ? ID : EN;
	const [choices, setChoices] = useState<ConsentChoices>(
		initial ?? ONLY_NECESSARY,
	);
	const dialogRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (isOpen) setChoices(initial ?? ONLY_NECESSARY);
	}, [isOpen, initial]);

	useEffect(() => {
		if (!isOpen) return;
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", handleEscape);
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", handleEscape);
			document.body.style.overflow = "";
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const toggle = (key: keyof ConsentChoices) => {
		if (key === "necessary") return;
		setChoices((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	const save = () => {
		const next = writeConsent(choices);
		trackConsentEvent("consent_categories_changed", {
			surface: "preferences_modal",
			trigger: "save_preferences",
			choices: next.choices,
			locale: locale === "id" ? "id" : "en",
		});
		onClose();
	};

	const rows: Array<{
		key: keyof ConsentChoices;
		title: string;
		desc: string;
		locked?: boolean;
	}> = [
		{
			key: "necessary",
			title: t.necessaryTitle,
			desc: t.necessaryDesc,
			locked: true,
		},
		{ key: "analytics", title: t.analyticsTitle, desc: t.analyticsDesc },
		{ key: "marketing", title: t.marketingTitle, desc: t.marketingDesc },
		{
			key: "preferences",
			title: t.preferencesTitle,
			desc: t.preferencesDesc,
		},
	];

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-200"
			role="dialog"
			aria-modal="true"
			aria-labelledby="cookie-prefs-title"
		>
			<div
				ref={dialogRef}
				className="bg-card rounded-2xl shadow-[0_5px_30px_rgba(0,0,0,0.22)] max-w-lg w-full p-6 sm:p-8 relative animate-in zoom-in-95 duration-200 max-h-[85vh] overflow-y-auto"
			>
				<button
					type="button"
					onClick={onClose}
					aria-label={t.close}
					className="absolute right-4 top-4 text-muted-foreground hover:text-foreground p-1 transition-colors cursor-pointer"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						<title>{t.close}</title>
						<path d="M18 6 6 18M6 6l12 12" />
					</svg>
				</button>

				<h2
					id="cookie-prefs-title"
					className="text-xl sm:text-2xl font-bold tracking-tight pr-8"
				>
					{t.title}
				</h2>
				<p className="text-sm text-muted-foreground mt-2">{t.description}</p>

				<ul className="mt-6 space-y-4">
					{rows.map((row) => (
						<li
							key={row.key}
							className="flex items-start justify-between gap-4 rounded-xl border border-border/60 p-4"
						>
							<div className="min-w-0">
								<p className="text-sm font-semibold tracking-tight">
									{row.title}
								</p>
								<p className="text-xs text-muted-foreground mt-1 leading-relaxed">
									{row.desc}
								</p>
							</div>
							{row.locked ? (
								<span className="text-xs font-medium text-muted-foreground whitespace-nowrap mt-1">
									{t.alwaysOn}
								</span>
							) : (
								<ConsentToggle
									checked={choices[row.key] === true}
									onChange={() => toggle(row.key)}
									label={row.title}
								/>
							)}
						</li>
					))}
				</ul>

				<div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
					<button
						type="button"
						onClick={onClose}
						className="w-full sm:w-auto border border-border/60 bg-transparent text-foreground font-semibold tracking-tight py-3 px-6 rounded-full hover:bg-muted/50 transition-colors cursor-pointer"
					>
						{t.cancel}
					</button>
					<button
						type="button"
						onClick={save}
						className="w-full sm:w-auto bg-primary text-primary-foreground font-semibold tracking-tight py-3 px-6 rounded-full hover:opacity-90 transition-opacity cursor-pointer"
					>
						{t.save}
					</button>
				</div>
			</div>

			<button
				type="button"
				className="fixed inset-0 -z-10 appearance-none bg-transparent border-none cursor-default w-full h-full"
				onClick={onClose}
				aria-label={t.close}
				tabIndex={-1}
			/>
		</div>
	);
}

function ConsentToggle({
	checked,
	onChange,
	label,
}: {
	checked: boolean;
	onChange: () => void;
	label: string;
}) {
	return (
		<button
			type="button"
			role="switch"
			aria-checked={checked}
			aria-label={label}
			onClick={onChange}
			className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
				checked ? "bg-primary" : "bg-muted"
			}`}
		>
			<span
				className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
					checked ? "translate-x-5" : "translate-x-0.5"
				}`}
			/>
		</button>
	);
}
